import { omit, values, mapValues, keyBy, flatMap } from 'lodash';
import safeToBn from '@blockcatio/tabby-library/lib/utilities/safeToBn';

import {
  ACCOUNT_CHANGED,
  ACCOUNT_WALLET_RESET,
  ACCOUNT_WALLET_SELECT,
  CAMPAIGN_CREATE_STARTED,
  CAMPAIGN_CREATE_SUBMITTED,
  CAMPAIGN_CREATE_ABORTED,
  CAMPAIGN_CREATE_FAILED,
  CAMPAIGN_CREATE_CONFIRMED,
  CAMPAIGN_CREATE_REJECTED,
  CAMPAIGNS_FETCHED,
  CAMPAIGNS_FETCH_FAILED,
  CAMPAIGN_LOCALLY_DISCARDED,
  CAMPAIGN_CANCEL_STARTED,
  CAMPAIGN_CANCEL_SUBMITTED,
  CAMPAIGN_CANCEL_ABORTED,
  CAMPAIGN_CANCEL_FAILED,
  CAMPAIGN_CANCEL_CONFIRMED,
  CAMPAIGN_CANCEL_REJECTED,
  REWARD_CANCEL_STARTED,
  REWARD_CANCEL_SUBMITTED,
  REWARD_CANCEL_ABORTED,
  REWARD_CANCEL_FAILED,
  REWARD_CANCEL_CONFIRMED,
  REWARD_CANCEL_REJECTED,
  REWARD_REDEEM_STARTED,
  REWARD_REDEEM_SUBMITTED,
  REWARD_REDEEM_ABORTED,
  REWARD_REDEEM_FAILED,
  REWARD_REDEEM_CONFIRMED,
  REWARD_REDEEM_REJECTED,
  CAMPAIGNS_LOCALLY_LOADED,
} from '../actionTypes';
import { CampaignStatuses, RewardStatuses } from '../constants';
import eventToConfirmedCampaign from '../utilities/eventToConfirmedCampaign';
import getCampaignStatus from '../utilities/getCampaignStatus';
import getRewardStatus from '../utilities/getRewardStatus';
import getCampaignIdFromEvent from '../utilities/getCampaignIdFromEvent';

const updateLocalCampaign = (state, id, modifier) => ({
  ...state,
  localCampaigns: {
    ...state.localCampaigns,
    [id]: modifier(state.localCampaigns[id] || {}),
  },
});

const moveCampaignToFetched = (state, id) => ({
  ...state,
  localCampaigns: omit(state.localCampaigns, id),
  fetchedCampaigns: {
    ...state.fetchedCampaigns,
    [id]: {
      ...(state.fetchedCampaigns[id] || {}),
      ...(state.localCampaigns[id] || {}),
    },
  },
  localRewards: {},
});

const discardLocalCampaign = (state, id) => ({
  ...state,
  localCampaigns: omit(state.localCampaigns, id),
});

const updateLocalReward = (state, rewardKey, modifier) => ({
  ...state,
  localRewards: {
    ...state.localRewards,
    [rewardKey]: modifier(state.localRewards[rewardKey] || {}, rewardKey),
  },
});

const moveRewardToFetched = (state, rewardKey) => ({
  ...state,
  localRewards: omit(state.localRewards, rewardKey),
  fetchedRewards: {
    ...state.fetchedRewards,
    [rewardKey]: {
      ...(state.fetchedRewards[rewardKey] || {}),
      ...(state.localRewards[rewardKey] || {}),
    },
  },
});

const discardLocalReward = (state, rewardKey) => ({
  ...state,
  localRewards: omit(state.localRewards, rewardKey),
});

const getInitialState = () => ({
  localCampaigns: {},
  fetchedCampaigns: {},
  localRewards: {},
  fetchedRewards: {},
  isFetchingCampaigns: false,
});

const reducer = (state = getInitialState(), action) => {
  const { type } = action;
  switch (type) {
    case ACCOUNT_CHANGED:
      return {
        ...state,
        localCampaigns: {},
        fetchedCampaigns: {},
        isFetchingCampaigns: true,
      };

    case ACCOUNT_WALLET_RESET:
      return {
        localCampaigns: {},
        fetchedCampaigns: {},
        isFetchingCampaigns: true,
      };

    case ACCOUNT_WALLET_SELECT:
      return {
        ...state,
        isFetchingCampaigns: true,
      };

    case CAMPAIGN_CREATE_STARTED:
      return updateLocalCampaign(state, 'draft', () => ({
        ...omit(action.campaign, ['codes', 'signer']),
        rewards: [],
        status: getCampaignStatus(CampaignStatuses.sending),
      }));

    case CAMPAIGN_CREATE_SUBMITTED:
      return {
        ...state,
        localCampaigns: omit(
          updateLocalCampaign(state, action.campaign.id, () => ({
            ...state.localCampaigns.draft,
            timestamp: action.campaign.timestamp,
            contract: action.contract,
            status: getCampaignStatus(CampaignStatuses.confirmingSend),
            transactionHash: action.transactionHash,
          })).localCampaigns,
          'draft',
        ),
      };

    case CAMPAIGN_CREATE_ABORTED:
    case CAMPAIGN_CREATE_FAILED:
      return discardLocalCampaign(state, 'draft');

    case CAMPAIGN_CREATE_CONFIRMED:
      return moveCampaignToFetched(
        updateLocalCampaign(state, getCampaignIdFromEvent(action.event), campaign => ({
          ...campaign,
          ...eventToConfirmedCampaign(action.event),
        })),
        getCampaignIdFromEvent(action.event),
      );

    case CAMPAIGN_CREATE_REJECTED:
      return updateLocalCampaign(state, action.campaignId, campaign => ({
        ...campaign,
        status: getCampaignStatus(CampaignStatuses.failed),
      }));

    case CAMPAIGNS_FETCHED:
      return {
        ...state,
        isFetchingCampaigns: false,
        fetchedCampaigns: mapValues(action.campaigns, campaign => ({
          ...campaign,
          rewards: campaign.rewards.map(reward => reward.rewardKey),
        })),
        fetchedRewards: keyBy(
          flatMap(values(action.campaigns), campaign => campaign.rewards),
          item => item.rewardKey,
        ),
      };

    case CAMPAIGNS_FETCH_FAILED:
      return {
        ...state,
        isFetchingCampaigns: false,
      };

    case CAMPAIGN_LOCALLY_DISCARDED:
      return discardLocalCampaign(state, action.campaignId);

    case CAMPAIGN_CANCEL_STARTED:
      return updateLocalCampaign(state, action.campaignId, campaign => ({
        ...campaign,
        status: getCampaignStatus(CampaignStatuses.cancelling),
      }));

    case CAMPAIGN_CANCEL_SUBMITTED:
      return updateLocalCampaign(state, action.campaignId, campaign => ({
        ...campaign,
        status: getCampaignStatus(CampaignStatuses.confirmingCancel),
      }));

    case CAMPAIGN_CANCEL_CONFIRMED: {
      return moveCampaignToFetched(
        updateLocalCampaign(state, action.campaignId, campaign => ({
          ...campaign,
          status: getCampaignStatus(CampaignStatuses.cancelled),
        })),
        action.campaignId,
      );
    }

    case CAMPAIGN_CANCEL_FAILED:
    case CAMPAIGN_CANCEL_ABORTED:
      return discardLocalCampaign(state, action.campaignId);

    case CAMPAIGN_CANCEL_REJECTED:
      return updateLocalCampaign(state, action.campaignId, campaign => ({
        ...campaign,
        status: getCampaignStatus(CampaignStatuses.failed),
      }));

    case REWARD_CANCEL_STARTED:
      return updateLocalReward(state, action.rewardKey, reward => ({
        ...reward,
        status: getRewardStatus(RewardStatuses.cancelling),
      }));

    case REWARD_CANCEL_SUBMITTED:
      return updateLocalReward(state, action.rewardKey, reward => ({
        ...reward,
        status: getRewardStatus(RewardStatuses.confirmingCancel),
      }));

    case REWARD_CANCEL_CONFIRMED: {
      return moveRewardToFetched(
        updateLocalReward(state, action.rewardKey, reward => ({
          ...reward,
          status: getRewardStatus(RewardStatuses.cancelled),
        })),
        action.rewardKey,
      );
    }

    case REWARD_CANCEL_FAILED:
    case REWARD_CANCEL_ABORTED:
      return discardLocalReward(state, action.rewardKey);

    case REWARD_CANCEL_REJECTED:
      return updateLocalReward(state, action.rewardKey, reward => ({
        ...reward,
        status: getRewardStatus(RewardStatuses.failed),
      }));

    case REWARD_REDEEM_STARTED:
      return updateLocalReward(state, action.rewardKey, reward => ({
        ...reward,
        rewardKey: action.rewardKey,
        status: getRewardStatus(RewardStatuses.redeeming),
      }));

    case REWARD_REDEEM_SUBMITTED:
      return updateLocalReward(state, action.rewardKey, reward => ({
        ...reward,
        status: getRewardStatus(RewardStatuses.confirmingRedeem),
      }));

    case REWARD_REDEEM_FAILED:
    case REWARD_REDEEM_ABORTED:
      return discardLocalReward(state, action.rewardKey);

    case REWARD_REDEEM_CONFIRMED:
      return moveRewardToFetched(
        updateLocalReward(state, action.rewardKey, reward => ({
          ...reward,
          status: getRewardStatus(RewardStatuses.redeemed),
        })),
        action.rewardKey,
      );

    case REWARD_REDEEM_REJECTED:
      return updateLocalReward(state, action.code, reward => ({
        ...reward,
        status: getRewardStatus(RewardStatuses.failed),
      }));

    case CAMPAIGNS_LOCALLY_LOADED:
      return {
        ...state,
        localCampaigns: mapValues(action.campaigns, campaign => ({
          ...campaign,
          // convert objects back from strings.
          tokensPerCode: safeToBn(campaign.tokensPerCode),
          status: getCampaignStatus(campaign.status),
        })),
      };

    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/campaigns.js